import angular, { IScope, isFunction } from "angular";
import { UserProfile } from "../../application/UserProfile";
import {
  CsvColumnConfig,
  CsvConfig,
  CsvWriter,
  vsName as CsvWriterName,
} from "../csv/csvWriter";
import { Record } from "../server/crud";
import { IQuery } from "../server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../server/serverUtility";
import { ValueGetter } from "./cells/valueGetters";
import { ColDef } from "./columns/colDef";
import { RecordDataSource } from "./recordDataSource";
import { IGridSettings } from "./settings/gridSettingsService";

export interface GridSelectionChangedArgs {
  data: Record;
  gridSelection: number[];
  selected: boolean;
}

export interface IRowCommandEventArgs<TRow> {
  commandName: string;
  data: TRow;
}

export class RecordGridController {
  static $inject = ["$scope", CsvWriterName, ServerUtilityName, "userProfile"];

  //bindings
  columns!: ColDef[];
  datasource!: RecordDataSource;
  gridSettings!: IGridSettings;
  defaultQuery: IQuery = {};
  defaultSort: string | undefined;
  gridSelectionChanged: (args: GridSelectionChangedArgs) => void = () => {};
  onItemCommand: (a: { args: IRowCommandEventArgs<unknown> }) => void =
    () => {};
  initialSelectedIds: number[] | undefined;
  showSelectButtons: boolean = false;
  showDownloadButton: boolean = true;

  currentQuery: IQuery = {};

  private selectedRowIds: number[] = [];

  private readonly gridOptions: awk.grid.GridOptions = {
    enableServerSideSorting: true,
    enableServerSideFilter: true,
    enableColResize: true,
    angularCompileRows: true,
    angularCompileFilters: true,
    showToolPanel: false,
    toolPanelSuppressValues: true,
    toolPanelSuppressPivot: true,
    ready: () => this.gridReady(),
    rowHeight: 40,
  };

  constructor(
    private readonly $scope: angular.IScope,
    private readonly csvWriter: CsvWriter,
    private readonly serverUtility: ServerUtility,
    private readonly userProfile: UserProfile
  ) {}

  $onInit() {
    this.$scope.$watch(
      () => this.initialSelectedIds,
      (newValue: number[] | undefined) => {
        this.setSelectedIds(newValue ?? []);
      }
    );

    this.gridOptions.columnDefs = this.columns.filter(
      (c) =>
        !c.requiresPermission ||
        this.userProfile.hasPermission(c.requiresPermission)
    );
    this.datasource.getOrderByField = (() => this.getOrderByString()).bind(
      this
    );
    const savedQuery = this.gridSettings.getQuery();
    this.currentQuery = this.getQuery(savedQuery);

    this.$scope.$watchCollection(
      () => this.currentQuery,
      () => this.queryChanged()
    );

    this.$scope.$watchCollection(
      () => this.defaultQuery,
      () => this.queryChanged()
    );

    this.$scope.$on("command", (e, args: IRowCommandEventArgs<any>) => {
      e.preventDefault();
      this.itemCommand(args.commandName, args.data);
    });
  }

  setSelectedIds(selectedIds: number[]) {
    this.selectedRowIds = selectedIds;
  }

  selectAllOnPage(select: boolean) {
    const pageRecords =
      this.gridOptions.api?.getRenderedNodes().map((node) => node.data) ?? [];

    const newSelections = pageRecords.filter(
      (r) => this.rowSelected(r) === !select
    );
    newSelections.forEach((r) => {
      this.toggleRowSelection(r);
    });
  }

  rowSelected(row: Record): boolean {
    return this.selectedRowIds?.includes(row.Id) ?? false;
  }

  toggleRowSelection(row: Record) {
    const id = row.Id;
    const collectionIndex = this.selectedRowIds.indexOf(id);
    const newSelection = collectionIndex < 0;
    if (newSelection) {
      this.selectedRowIds.push(id);
    } else {
      this.selectedRowIds.splice(collectionIndex, 1);
    }

    this.gridSelectionChanged({
      data: row,
      gridSelection: [...this.selectedRowIds],
      selected: newSelection,
    });
  }

  private queryChanged(): void {
    this.gridSettings.saveQuery(this.currentQuery);

    const newQuery = this.getQuery(this.currentQuery);
    this.datasource.query = newQuery;
    this.gridOptions.api?.onNewDatasource();
  }

  private getQuery(query: IQuery) {
    return { ...query, ...this.defaultQuery };
  }

  private getOrderByString(): string | undefined {
    const sortModel = this.gridOptions.api?.getSortModel();

    if (!sortModel || sortModel.length === 0) {
      return this.defaultSort;
    }

    const orderFields = sortModel.map((orderField: any) => {
      const { colId, sort: direction } = orderField;
      const sortAscending = !direction || direction !== "desc";
      return sortAscending ? colId : "-" + colId;
    });
    const orderByString = orderFields.reduce(
      (result: string, field: string) => result + `${field}`
    );
    return this.defaultSort === undefined || this.defaultSort === ""
      ? orderByString
      : `${orderByString},${this.defaultSort}`;
  }

  private itemCommand(commandName: string, data: Record) {
    if (!this.onItemCommand) return;
    this.onItemCommand({
      args: {
        commandName,
        data,
      },
    });
  }

  clearFilters() {
    this.currentQuery = {};
  }

  toggleGridSettings() {
    const gridApi = this.gridOptions.api;
    if (!gridApi) {
      return;
    }
    const isToolPanelShown = gridApi.isToolPanelShowing();
    gridApi.showToolPanel(!isToolPanelShown);
  }

  private gridReady() {
    const columnApi = this.gridOptions.columnApi;
    if (columnApi) {
      this.gridSettings.syncColumns(columnApi);
      columnApi.addChangeListener(() => {
        this.gridSettings.saveState(columnApi);
      });
    }

    this.gridOptions.api?.setDatasource(this.datasource);
  }

  downloadCsv() {
    const columns = this.getCsvColumns();
    const query = this.getQuery(this.currentQuery);
    query.OrderBy = this.getOrderByString();
    query.Skip = 0;

    const request = { url: this.datasource.url, query };
    const serverPromise = this.datasource.useWrappedQuery
      ? this.serverUtility.fetchAllWrapped(request)
      : this.serverUtility.fetchAll(request);

    serverPromise.then((rows) => {
      const csvConfig: CsvConfig = {
        columns,
        includeHeaders: true,
      };
      const csv = this.csvWriter.getCsv(csvConfig, rows);
      this.csvWriter.saveFile({ content: csv, filename: "out.csv" });
    });
  }

  getCsvColumns(): CsvColumnConfig[] {
    const gridApi = this.gridOptions.api;
    if (!gridApi) {
      throw new Error("Could not get grid Api to build Csv column configs");
    }
    const columns = this.gridOptions.columnApi?.getState() ?? [];
    const colDefs = columns
      .filter((c) => !c.hide)
      .map((c) => gridApi.getColumnDef(c.colId));
    return colDefs
      .filter((c) => c.field)
      .map((c) => this.getCsvColumnFromGridColDef(c));
  }

  getCsvColumnFromGridColDef(colDef: ColDef): CsvColumnConfig {
    return {
      field: colDef.field!,
      header: colDef.headerName,
      formatter: (params) => this.getFormattedValue(params.row, colDef),
    };
  }

  private getFormattedValue(row: any, colDef: ColDef): string | undefined {
    if (isFunction(colDef.valueGetter)) {
      const value = (<ValueGetter>colDef.valueGetter)({ data: row, colDef });
      return (<any>value)?.toString();
    }
    return row[colDef.field!]?.toString();
  }
}
